import { useCallback, useMemo, useState } from 'react';
import { Form, useFormikContext } from 'formik';
import { Text } from '@antimatterhq/ui/components/typography';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Button } from '@antimatterhq/ui/components/button';
import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { PreTransformAdditionalBronzeTables } from './PreTransformAdditionalBronzeTables';
import PreTransformFilters from './PreTransformFilters';
import { FieldSpecTable } from '@/components/table/FieldSpecTable/FieldSpecTable';
import {
  PreTransformSchemaFormValues,
  PreTransformSchemaShape,
} from '@/zod/schemas/dataSource/PreTransform';
import CustomFieldDialog from '@/components/dialog/customField/CustomField';
import { DataSourceNavigationButtons } from '@/components/button/dataSourceNavigation/DataSourceNavigationButtons';
import {
  DatasourceSubmitType,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import {
  addFieldSchemaValue,
  applyPreTransformPreset,
  deleteFieldSchemaValue,
  generateNotebookName,
  getCopyableFields,
  getFieldSchemaValue,
  getFieldTableRows,
  getTakenNames,
  setPreTransformCustomFunctionRequest,
  updateFieldSchemaValue,
} from '@/lib/datasource';
import { PreTransformPresetsDropdown } from '@/components/dropdown';
import { JSONExtraction } from '@/components/dataSources/jsonExtract/JSONExtract';
import { UnreferencedFields } from '@/components/dataSources/unreferencedFields/UnreferencedFields';
import { FormError } from '@/components/form/form';
import { useCreateNotebookFunction } from '@/hooks/dbui/useCreateNotebookFunction';
import {
  convertToCustomFunctionTooltip,
  unreferencedFieldsTooltip,
} from '@/strings/StringsTooltips';
import { CustomNotebookFunction } from '@/components/dataSources/customNotebookFunction/CustomNotebookFunction';
import { filterNulls } from '@/utils/filterNulls';
import { getFormikNames } from '@/utils/util';
import { FieldSpecSchemaValues } from '@/zod/schemas/dataSource/Common';
import { useCreateNotebook } from '@/hooks/dbui/useCreateNotebook';
import {
  FormAccordion,
  FormAccordionContent,
  FormAccordionItem,
  FormAccordionTrigger,
} from '@/components/form/accordion/FormAccordion';
import { Datasource_FieldSpec } from '@/types/api';
import { DatasourceTemporaryFields } from '@/components/dataSources/temporaryFields/DatasourceTemporaryFields';

export type PreTransformFormProps = {
  submitAction: DatasourceSubmitType;
  setSubmitAction: (action: DatasourceSubmitType) => void;
  submitDisabled?: boolean;
};

type PreTransformKeys = keyof typeof PreTransformSchemaShape.shape;

export const PreTransformForm = ({
  submitAction,
  setSubmitAction,
  submitDisabled = false,
}: PreTransformFormProps) => {
  const { preset } = useConfigDataSource();
  const { dataSource, handlePreviousStep, isSaving } = useConfigDataSource();
  const { enableJsonExtraction, enableTemporaryFields } = useFlags<FlagSet>();

  const [customField, setCustomField] = useState<
    FieldSpecSchemaValues | undefined
  >(undefined);

  const {
    mutateAsync: createNotebookFunction,
    isPending: isCreatingNotebookFunction,
  } = useCreateNotebookFunction();
  const {
    mutateAsync: createCustomNotebook,
    isPending: isCreatingCustomNotebook,
  } = useCreateNotebook();

  const formik = useFormikContext<PreTransformSchemaFormValues>();
  const names = useMemo(
    () => getFormikNames<PreTransformKeys>(PreTransformSchemaShape.shape, ''),
    [],
  );

  const { values, handleChange, setFieldValue, isSubmitting, setValues } =
    formik;

  const preTransformPreset = useMemo(
    () =>
      preset?.silver?.preTransform?.find(
        ({ name }) => name === values.usePreset,
      ),
    [preset, values.usePreset],
  );

  const fieldSpecData = useMemo(
    () =>
      getFieldTableRows(
        filterNulls(preTransformPreset?.fields),
        filterNulls(values.addFields),
        filterNulls(values.omitFields),
      ),
    [values, preTransformPreset],
  );

  const takenNames = useMemo(
    () => getTakenNames(fieldSpecData),
    [fieldSpecData],
  );

  const copyableFields = useMemo(
    () => getCopyableFields(fieldSpecData),
    [fieldSpecData],
  );

  const onPresetToggle = useCallback(
    (field: Datasource_FieldSpec, enabled: boolean) => {
      if (!field.name) throw new Error('fieldName is required');
      const value = [...(values.omitFields || [])];
      if (enabled) {
        value.splice(value.indexOf(field.name!), 1);
      } else {
        value.push(field.name!);
      }
      handleChange({
        target: {
          name: names.omitFields,
          value,
        },
      });
    },
    [values.omitFields, handleChange, names],
  );

  const addField = useCallback(
    (fieldSpec: Datasource_FieldSpec) => {
      const addFields = [...(values.addFields || []), { ...fieldSpec }];
      setFieldValue(names.addFields, addFields);
      addFieldSchemaValue(fieldSpec, values.addFields);
    },
    [values.addFields, setFieldValue, names],
  );

  const updateField = useCallback(
    (fieldSpec: Datasource_FieldSpec) =>
      setFieldValue(
        names.addFields,
        updateFieldSchemaValue(fieldSpec, customField, values.addFields),
      ),
    [values.addFields, setFieldValue, customField, names],
  );

  const onEditField = useCallback(
    (source: {
      preset?: Datasource_FieldSpec;
      custom?: FieldSpecSchemaValues;
    }) => {
      let custom = source.custom;
      if (!custom) {
        custom = getFieldSchemaValue(source.preset);
      }
      setCustomField(custom);
    },
    [],
  );

  const onDeleteField = useCallback(
    (field: FieldSpecSchemaValues) => {
      if (!field.name) throw new Error('fieldName is required');
      setFieldValue(
        names.addFields,
        deleteFieldSchemaValue(field.name!, filterNulls(values.addFields)),
      );
    },
    [values.addFields, setFieldValue, names],
  );

  const onCreateCustomNotebook = useCallback(async () => {
    let customNotebookPath = values.customNotebookPath;
    if (!customNotebookPath) {
      const path = generateNotebookName(dataSource.metadata?.name || '');
      await createCustomNotebook(
        { path, templateType: 'datasource' },
        {
          onSuccess: async ({ data }) => {
            customNotebookPath = data.notebookPath;
            setFieldValue('customNotebookPath', customNotebookPath);
          },
          toasts: {
            success: 'Notebook created!',
            error: 'Failed to create notebook',
          },
        },
      );
    }
    const request = setPreTransformCustomFunctionRequest(dataSource, {
      ...values,
      customNotebookPath,
    });
    createNotebookFunction(request, {
      onSuccess: async ({ data }) => {
        setFieldValue('customFunction', request.name);
        setFieldValue('method', 'custom');
        window.open(data.notebookUrl, '_blank', 'noopener,noreferrer');
      },
      toasts: {
        success: 'Notebook function created',
        error: 'Failed to create notebook function',
      },
    });
  }, [
    createNotebookFunction,
    dataSource,
    values,
    setFieldValue,
    createCustomNotebook,
  ]);

  const onPresetChange = useCallback(
    (value: string) => {
      const newPreTransformPreset = preset?.silver?.preTransform?.find(
        ({ name }) => name === value,
      );
      setValues(
        applyPreTransformPreset(values, newPreTransformPreset, value),
        false,
      );
    },
    [preset, setValues, values],
  );

  return (
    <>
      <Form>
        <div>
          <Text size="lg" bold>
            Bronze table: {dataSource.spec?.bronze?.bronzeTable}
          </Text>

          <Spacer size="lg" />

          <Text size="lg" bold>
            Additional bronze tables
          </Text>

          <PreTransformAdditionalBronzeTables />

          <Spacer size="lg" />
          <Text size="lg" bold>
            Pre-transform details
          </Text>
          <Spacer size="lg" />

          <Label>Pre-transform method</Label>

          <ButtonGroup
            options={[
              {
                label: dataSource.spec.usePreset
                  ? 'Configure preset & fields'
                  : 'Configure fields',
                value: 'configure',
              },
              {
                label: 'No pre-transform',
                value: 'none',
              },
              {
                label: 'Custom function',
                value: 'custom',
              },
            ]}
            value={values.method}
            onChange={(value) => setFieldValue(names.method, value)}
            data-testid="pre-transform-method-button-group"
          />

          {values.method === 'custom' && (
            <>
              <Spacer size="lg" />
              <CustomNotebookFunction
                values={{
                  ...values,
                  name: dataSource.metadata?.name || '',
                }}
                currentTable="pre-transform"
                notebookPath={values.customNotebookPath}
              />
            </>
          )}

          {values.method === 'configure' && (
            <>
              <Spacer size="lg" />

              <Label>Pre-transform preset</Label>

              <PreTransformPresetsDropdown
                data-testid="pre-transform-preset-dropdown"
                value={values.usePreset}
                onValueChange={(value) => {
                  onPresetChange(value);
                }}
                name={dataSource.spec.usePreset}
              />
              <FormError name={names.usePreset} />

              <Text size="lg" className="mt-4 block">
                Current field list
              </Text>

              <FieldSpecTable
                data={fieldSpecData}
                onToggle={onPresetToggle}
                onEdit={onEditField}
                onDelete={onDeleteField}
                disabled={isSaving}
              />
              <Spacer size="base" />

              <Button
                leftIcon="radix-icons:plus"
                variant="default"
                className="w-fit"
                onClick={() => setCustomField({})}
                type="button"
                data-testid="add-field-button"
              >
                Add a Field
              </Button>

              <Spacer size="base" />

              <FormAccordion type="multiple">
                <FormAccordionItem value="filters">
                  <FormAccordionTrigger data-testid="filters-accordion-trigger">
                    <Text size="lg">Filters</Text>
                  </FormAccordionTrigger>
                  <FormAccordionContent>
                    <PreTransformFilters />
                  </FormAccordionContent>
                </FormAccordionItem>

                <FormAccordionItem value="preserve">
                  <FormAccordionTrigger data-testid="unreferenced-fields-accordion-trigger">
                    <InfoTooltip
                      content={unreferencedFieldsTooltip}
                      trigger={
                        <Text size="lg">Preserve unreferenced fields</Text>
                      }
                    />
                  </FormAccordionTrigger>
                  <FormAccordionContent className="-m-2 p-2">
                    <UnreferencedFields unreferencedFields={values} />
                  </FormAccordionContent>
                </FormAccordionItem>

                {enableJsonExtraction && (
                  <FormAccordionItem value="json">
                    <FormAccordionTrigger data-testid="json-extraction-accordion-trigger">
                      <Text size="lg">JSON extraction</Text>
                    </FormAccordionTrigger>
                    <FormAccordionContent className="-m-2 p-2">
                      <JSONExtraction jsonExtract={values.jsonExtract} />
                    </FormAccordionContent>
                  </FormAccordionItem>
                )}

                {enableTemporaryFields && (
                  <FormAccordionItem value="temporaryFields">
                    <FormAccordionTrigger data-testid="temporary-fields-accordion-trigger">
                      <Text size="lg">Temporary fields</Text>
                    </FormAccordionTrigger>
                    <FormAccordionContent className="-m-2 p-2">
                      <DatasourceTemporaryFields
                        values={values}
                        prefix={''}
                        disabled={isSaving}
                        currentTable={'silver'}
                        previousTable={'bronze'}
                      />
                    </FormAccordionContent>
                  </FormAccordionItem>
                )}
              </FormAccordion>

              <Spacer size="lg" />
              <InfoTooltip
                content={convertToCustomFunctionTooltip}
                trigger={
                  <Button
                    variant="default"
                    onClick={() => onCreateCustomNotebook()}
                    disabled={
                      !dataSource.spec?.bronze?.bronzeTable ||
                      isSubmitting ||
                      isCreatingNotebookFunction ||
                      isCreatingCustomNotebook
                    }
                    leftIcon="lucide:notebook"
                    loading={
                      isCreatingNotebookFunction || isCreatingCustomNotebook
                    }
                    data-testid="convert-to-custom-function-button"
                  >
                    Convert to custom function
                  </Button>
                }
              />
            </>
          )}

          <Spacer size="2xl" />

          <DataSourceNavigationButtons
            disabled={isSaving || submitDisabled}
            goToPreviousStep={handlePreviousStep}
            handlePreviousStep={() => setSubmitAction('previous')}
            handleNextStep={() => setSubmitAction('next')}
            handleReview={() => setSubmitAction('review')}
            loadingNext={submitAction === 'next' && isSaving}
            loadingReview={submitAction === 'review' && isSaving}
          />
        </div>
      </Form>
      {customField && (
        <CustomFieldDialog
          currentTable="pre-transform"
          currentTableFields={copyableFields.filter(
            (name) => name !== customField.name,
          )}
          previousTable={'bronze'}
          onSubmit={(fieldSpec: Datasource_FieldSpec) => {
            if (Object.keys(customField).length === 0) {
              addField(fieldSpec);
            } else {
              updateField(fieldSpec);
            }
          }}
          fieldSpec={customField}
          open={true}
          onClose={() => setCustomField(undefined)}
          takenNames={takenNames.filter((name) => name !== customField.name)}
        />
      )}
    </>
  );
};
